import { useQuery } from '@tanstack/react-query';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db, auth } from '@/backend/lib/firebase';
import type { Booking, Contract } from '@/shared/types';

export interface OccupiedRange {
  start: Date;
  end: Date;
  source: 'contract' | 'booking';
}

export interface TypologyOccupancy {
  // Date ranges where ALL units in the typology are occupied — drives
  // the date picker so a date only greys out when no unit is free.
  occupiedRanges: OccupiedRange[];
  // Per-unit ranges, keyed by roomTypeId — used at submit time to pick
  // a still-free unit to assign to the booking.
  unitRanges: Record<string, OccupiedRange[]>;
}

/**
 * Returns occupancy for one or more units in the same typology.
 *
 * `occupiedRanges` is the intersection across units (dates when ALL are
 * taken) and drives the date picker. `unitRanges` is the raw per-unit data
 * used at submit time to pick which specific unit to assign to the booking.
 *
 * Terminated contracts are included but their range is capped at terminatedAt.
 * Stale approved bookings are also capped if a terminated contract covered them,
 * freeing the dates after early termination for new bookings.
 */
export const useRoomTypeOccupancy = (roomTypeIds: string[] | undefined) => {
  const stableIds = roomTypeIds ? [...roomTypeIds].sort() : [];

  return useQuery({
    queryKey: ['roomTypeOccupancy', stableIds],
    queryFn: async (): Promise<TypologyOccupancy> => {
      const unitRanges: Record<string, OccupiedRange[]> = {};
      for (const id of stableIds) unitRanges[id] = [];

      if (stableIds.length === 0) {
        return { occupiedRanges: [], unitRanges };
      }

      // All contracts (active AND terminated) for any of these units
      const contractsSnap = await getDocs(
        query(collection(db, 'contracts'), where('roomTypeId', 'in', stableIds))
      );
      const contracts: Contract[] = contractsSnap.docs.map(
        doc => ({ id: doc.id, ...doc.data() } as Contract)
      );
      const terminatedContracts = contracts.filter(c => c.status === 'terminated');

      for (const c of contracts) {
        const effectiveEnd = c.status === 'terminated' && c.terminatedAt
          ? c.terminatedAt.toDate()
          : c.endDate.toDate();
        unitRanges[c.roomTypeId]?.push({
          start: c.startDate.toDate(),
          end: effectiveEnd,
          source: 'contract',
        });
      }

      // Approved / pending bookings
      const bookingsSnap = await getDocs(
        query(
          collection(db, 'bookings'),
          where('roomTypeId', 'in', stableIds),
          where('status', 'in', ['approved', 'blocked'])
        )
      );
      for (const doc of bookingsSnap.docs) {
        const b = { id: doc.id, ...doc.data() } as Booking;
        if (!b.roomTypeId) continue;
        const checkIn = b.checkIn.toDate();
        const checkOut = b.checkOut.toDate();

        // Cap the booking's effective end at terminatedAt if a terminated
        // contract on the same unit covered this booking:
        //   c.startDate <= b.checkIn < c.terminatedAt < b.checkOut
        let effectiveEnd = checkOut;
        for (const c of terminatedContracts) {
          if (c.roomTypeId !== b.roomTypeId || !c.terminatedAt) continue;
          const cStart = c.startDate.toDate();
          const cTerminated = c.terminatedAt.toDate();
          if (cStart <= checkIn && checkIn < cTerminated && cTerminated < checkOut) {
            effectiveEnd = new Date(Math.min(effectiveEnd.getTime(), cTerminated.getTime()));
          }
        }

        unitRanges[b.roomTypeId]?.push({
          start: checkIn,
          end: effectiveEnd,
          source: 'booking',
        });
      }

      for (const id of stableIds) {
        unitRanges[id].sort((a, b) => a.start.getTime() - b.start.getTime());
      }

      const occupiedRanges = intersectOccupiedRanges(stableIds.map(id => unitRanges[id]));

      return { occupiedRanges, unitRanges };
    },
    // The bookings/contracts collections require auth to read (Firestore rules).
    // Unauthenticated guests on the public booking form must NOT run this query
    // or it fails with permission-denied. Guests still get correct availability
    // from the getAvailableRooms Cloud Function; the calendar simply won't grey
    // individual occupied ranges for them.
    enabled: !!roomTypeIds && roomTypeIds.length > 0 && !!auth.currentUser,
  });
};

// Returns date ranges during which every unit is occupied.
export function intersectOccupiedRanges(perUnit: OccupiedRange[][]): OccupiedRange[] {
  if (perUnit.length === 0) return [];
  if (perUnit.length === 1) return perUnit[0];

  const events = new Set<number>();
  for (const ranges of perUnit) {
    for (const r of ranges) {
      events.add(r.start.getTime());
      events.add(r.end.getTime());
    }
  }
  const sorted = Array.from(events).sort((a, b) => a - b);

  const result: OccupiedRange[] = [];
  let activeStart: number | null = null;

  for (let i = 0; i < sorted.length - 1; i++) {
    const intervalStart = sorted[i];
    const intervalEnd = sorted[i + 1];
    if (intervalEnd <= intervalStart) continue;

    const allOccupied = perUnit.every(ranges =>
      ranges.some(r =>
        r.start.getTime() <= intervalStart && r.end.getTime() >= intervalEnd
      )
    );

    if (allOccupied && activeStart === null) {
      activeStart = intervalStart;
    } else if (!allOccupied && activeStart !== null) {
      result.push({
        start: new Date(activeStart),
        end: new Date(intervalStart),
        source: 'booking',
      });
      activeStart = null;
    }
  }

  if (activeStart !== null) {
    result.push({
      start: new Date(activeStart),
      end: new Date(sorted[sorted.length - 1]),
      source: 'booking',
    });
  }

  return result;
}

/**
 * Picks the first unit ID with no booking overlap for [checkIn, checkOut].
 * Iterates in the given order, so callers should pass `unitIds` already
 * sorted by their preferred priority (e.g. oldest first).
 */
export function findFirstAvailableUnit(
  unitIds: string[],
  unitRanges: Record<string, OccupiedRange[]>,
  checkIn: Date,
  checkOut: Date
): string | null {
  for (const id of unitIds) {
    const ranges = unitRanges[id] ?? [];
    const hasConflict = ranges.some(r => checkIn < r.end && checkOut > r.start);
    if (!hasConflict) return id;
  }
  return null;
}
